import { syncMediaCaption } from '@/lib/admin-api';
import { isGifUrl } from '@/lib/image-media-utils';

const MEDIA_FOLDERS = [
  'haber',
  'manset',
  'video',
  'icerik',
  'user-submissions',
] as const;

/**
 * Kendi depo: haber/2026/08/12/file.jpg
 * DonanımHaber …/haber/209204/… harici path’lerini yutma (sitede 404).
 */
const OWN_MEDIA_KEY_PATTERN =
  /^(?:storage\/)?((?:haber|manset|video|icerik|user-submissions)\/20\d{2}\/(0[1-9]|1[0-2])(?:\/(?:0[1-9]|[12]\d|3[01]))?\/[^\s?#]+)/i;

function isOwnMediaHostname(hostname: string): boolean {
  const h = hostname.toLowerCase().replace(/^www\./, '');
  return (
    h === 'haber.com' ||
    h.endsWith('.haber.com') ||
    h.endsWith('.cloudfront.net') ||
    h === 'localhost' ||
    h.endsWith('.local')
  );
}

function matchOwnMediaKey(candidate: string): string | null {
  const cleaned = candidate.replace(/^\/+/, '').replace(/^storage\//i, '');
  const m = OWN_MEDIA_KEY_PATTERN.exec(cleaned);
  return m?.[1] || null;
}

export function extractRelativeMediaPath(src: string): string | null {
  const trimmed = src.trim();
  if (!trimmed || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
    return null;
  }

  const noQuery = trimmed.split('?')[0].split('#')[0];
  const cleaned = noQuery.replace(/^\/+/, '').replace(/^storage\//i, '');

  const fromRelative = matchOwnMediaKey(cleaned);
  if (fromRelative) return fromRelative;

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const url = new URL(trimmed);
      const fromQuery = url.searchParams.get('path')?.trim();
      if (fromQuery) {
        const qKey = matchOwnMediaKey(fromQuery);
        if (qKey) return qKey;
      }
      // Harici host’taki /haber/ segmenti bizim depo değil (DonanımHaber vb.)
      if (!isOwnMediaHostname(url.hostname)) {
        return null;
      }
      const pathKey = matchOwnMediaKey(url.pathname.replace(/^\/+/, ''));
      if (pathKey) return pathKey;
      const parts = url.pathname.split('/').filter(Boolean);
      const folderIdx = parts.findIndex((part) =>
        (MEDIA_FOLDERS as readonly string[]).includes(
          part as (typeof MEDIA_FOLDERS)[number],
        ),
      );
      if (folderIdx >= 0) {
        return matchOwnMediaKey(parts.slice(folderIdx).join('/'));
      }
    } catch {
      return null;
    }
  }

  return null;
}

export async function syncMediaCaptionFromEditor(
  src: string,
  caption: string,
): Promise<void> {
  const trimmed = caption.trim();
  if (!trimmed || isGifUrl(src)) return;

  const path = extractRelativeMediaPath(src);
  if (!path) return;

  try {
    await syncMediaCaption(path, trimmed);
  } catch {
    // Sessiz — kayıt sırasında toplu senkron yapılır
  }
}

export function collectCaptionPairsFromDocument(
  doc: Document,
): Array<{ path: string; caption: string }> {
  const pairs: Array<{ path: string; caption: string }> = [];
  const seen = new Set<string>();

  doc.body.querySelectorAll('figure').forEach((figure) => {
    const img = figure.querySelector('img');
    const figcaption = figure.querySelector('figcaption');
    if (!img || !figcaption) return;

    const src = img.getAttribute('src') || '';
    if (isGifUrl(src)) return;

    const path = extractRelativeMediaPath(src);
    if (!path) return;

    const caption = figcaption.textContent?.trim() || '';
    if (!caption || seen.has(path)) return;

    seen.add(path);
    pairs.push({ path, caption });
  });

  return pairs;
}
